export type ForwardHeaders = {
  from: string | null;
  date: string | null;
  to: string | null;
  cc: string | null;
  subject: string | null;
};

export type ForwardedBlock = { headers: ForwardHeaders; body: string };

export type EmailSplit = {
  main: string;
  quoted: string | null;
  forwarded: ForwardedBlock | null;
};

export const FORWARD_MARKERS: RegExp[] = [
  /^-{2,}\s*Forwarded message\s*-{2,}/i,
  /^Begin forwarded message:?/i,
  /^-{2,}\s*Weitergeleitete Nachricht\s*-{2,}/i,
  /^-{2,}\s*Mensaje reenviado\s*-{2,}/i,
  /^-{2,}\s*Message transféré\s*-{2,}/i,
];

const REPLY_HEADER_MARKERS: RegExp[] = [
  /^On\s.{0,200}?\s(?:wrote|escribió|a écrit|schrieb|napisał):?$/i,
  /^-{2,}\s*Original Message\s*-{2,}$/i,
  /^_{5,}$/,
];

const FROM_LINE = /^From:\s*\S/i;
const HEADER_FIELD = /(?:^|\s)(?:Sent|Date|To|Subject|Cc):\s*\S/i;
const HEADER_FIELD_LINE = /^\s*(?:Sent|Date|To|Subject|Cc):\s*\S/i;

const HEADER_LINE = /^(From|De|Von|Sent|Date|Fecha|Datum|To|Para|An|Cc|Subject|Asunto|Betreff|Objet)\s*:\s*(.*)$/i;

const HEADER_KEYS: Record<string, keyof ForwardHeaders> = {
  from: "from",
  de: "from",
  von: "from",
  sent: "date",
  date: "date",
  fecha: "date",
  datum: "date",
  to: "to",
  para: "to",
  an: "to",
  cc: "cc",
  subject: "subject",
  asunto: "subject",
  betreff: "subject",
  objet: "subject",
};

const EMPTY_HEADERS: ForwardHeaders = {
  from: null,
  date: null,
  to: null,
  cc: null,
  subject: null,
};

const STRONG_QUOTE_SELECTORS = [
  ".gmail_quote_container",
  ".gmail_quote",
  "div.yahoo_quoted",
  "blockquote[type='cite']",
  "#divRplyFwdMsg",
  ".OutlookMessageHeader",
  "#Signature + hr",
];

const WEAK_QUOTE_SELECTOR = "blockquote";
const BLOCK_TAGS = new Set(["DIV", "P", "TR", "LI", "H1", "H2", "H3", "H4", "H5", "H6"]);

const MAX_MARKER_LEN = 300;
const TRAILING_SLACK = 200;
const DOC_POSITION_FOLLOWING = 4;
const MAX_HEADER_SIBLINGS = 12;
const MAX_UNWRAP_DEPTH = 4;

function norm(raw: string | null | undefined): string {
  return (raw ?? "").replace(/\s+/g, " ").trim();
}

function dense(raw: string | null | undefined): number {
  return (raw ?? "").replace(/[\s\u00a0]+/g, "").length;
}

function startsAsForward(raw: string | null | undefined): boolean {
  const t = norm(raw);
  return FORWARD_MARKERS.some((re) => re.test(t));
}

function hasCompanionHeader(el: Element): boolean {
  let sib = el.nextElementSibling;
  for (let i = 0; sib && i < 4; i++) {
    if (HEADER_FIELD_LINE.test(norm(sib.textContent))) return true;
    sib = sib.nextElementSibling;
  }
  return false;
}

function isAttributionText(t: string): boolean {
  if (!t || t.length > MAX_MARKER_LEN) return false;
  if (REPLY_HEADER_MARKERS.some((re) => re.test(t))) return true;
  return FROM_LINE.test(t) && HEADER_FIELD.test(t);
}

function isAttributionEl(el: Element): boolean {
  const t = norm(el.textContent);
  if (!t || t.length > MAX_MARKER_LEN) return false;
  if (REPLY_HEADER_MARKERS.some((re) => re.test(t))) return true;
  if (!FROM_LINE.test(t)) return false;
  return HEADER_FIELD.test(t) || hasCompanionHeader(el);
}

function findTextMarker(body: HTMLElement): Element | null {
  for (const el of Array.from(body.querySelectorAll("*"))) {
    if (!isAttributionEl(el)) continue;
    const nested = Array.from(el.children).some((c) => isAttributionEl(c));
    if (!nested) return el;
  }
  return null;
}

function findForwardMarker(body: HTMLElement): Element | null {
  let outermost: Element | null = null;
  for (const el of Array.from(body.querySelectorAll("*"))) {
    if (!startsAsForward(el.textContent)) continue;
    const nested = Array.from(el.children).some((c) => startsAsForward(c.textContent));
    if (!nested) return el;
    outermost ??= el;
  }
  return outermost;
}

function earliest(a: Element | null, b: Element | null): Element | null {
  if (!a) return b;
  if (!b || a === b) return a;
  return a.compareDocumentPosition(b) & DOC_POSITION_FOLLOWING ? a : b;
}

function hasAttributionLead(el: Element): boolean {
  let prev = el.previousElementSibling;
  while (prev) {
    const t = norm(prev.textContent);
    if (prev.tagName === "HR" || !t) {
      prev = prev.previousElementSibling;
      continue;
    }
    return isAttributionText(t);
  }
  return false;
}

/**
 * A bare <blockquote> is used both for reply history and for ordinary inline
 * quotation. Only treat it as a cut point when an attribution line precedes it,
 * or when nothing but a signature-sized tail follows it.
 */
function isLikelyReplyQuote(body: HTMLElement, el: Element, doc: Document): boolean {
  if (hasAttributionLead(el)) return true;

  const before = doc.createRange();
  before.setStart(body, 0);
  before.setEndBefore(el);
  if (dense(before.toString()) === 0) return false;

  const after = doc.createRange();
  after.setStartAfter(el);
  after.setEnd(body, body.childNodes.length);
  return dense(after.toString()) <= TRAILING_SLACK;
}

function findMarker(body: HTMLElement, doc: Document): Element | null {
  let marker: Element | null = null;

  for (const sel of STRONG_QUOTE_SELECTORS) {
    marker = earliest(marker, body.querySelector(sel));
  }
  if (marker) return marker;

  for (const bq of Array.from(body.querySelectorAll(WEAK_QUOTE_SELECTOR))) {
    if (isLikelyReplyQuote(body, bq, doc)) marker = earliest(marker, bq);
  }
  return earliest(marker, findTextMarker(body));
}

function backUpToLead(marker: Element): Element {
  let cut = marker;
  let prev = marker.previousElementSibling;

  while (prev) {
    const t = norm(prev.textContent);
    const skippable = prev.tagName === "HR" || !t || isAttributionText(t);
    if (!skippable) break;
    cut = prev;
    prev = prev.previousElementSibling;
  }

  return cut;
}

/**
 * Moves the marker and everything after it out of the body, rebuilding the
 * ancestor wrappers around the removed content. Nodes are moved rather than
 * cloned, so references held by the caller stay valid inside the returned holder.
 */
function extractTail(marker: Element, body: HTMLElement, doc: Document): HTMLElement {
  let holder = doc.createElement("div");
  let node: Element | null = marker;
  let includeSelf = true;

  while (node !== null && node !== body) {
    const parent: Element | null = node.parentElement;
    const batch: Node[] = [];
    let sib: Node | null = includeSelf ? node : node.nextSibling;

    while (sib !== null) {
      batch.push(sib);
      sib = sib.nextSibling;
    }
    batch.forEach((n) => holder.appendChild(n));

    if (parent && parent !== body) {
      const shell = parent.cloneNode(false) as Element;
      shell.removeAttribute("id");
      while (holder.firstChild) shell.appendChild(holder.firstChild);
      const next = doc.createElement("div");
      next.appendChild(shell);
      holder = next;
    }

    includeSelf = false;
    node = parent;
  }

  return holder;
}

function isBlank(node: Node): boolean {
  if (dense(node.textContent) > 0) return false;
  if (node.nodeType !== 1) return true;
  return (node as Element).querySelector("img, table, video") === null;
}
const MAX_PRUNE_DEPTH = 4;

function pruneEdges(el: Element, depth = MAX_PRUNE_DEPTH): void {
  while (el.firstChild && isBlank(el.firstChild)) el.firstChild.remove();
  while (el.lastChild && isBlank(el.lastChild)) el.lastChild.remove();

  if (depth <= 0) return;
  const kids = Array.from(el.children);
  if (kids.length === 1) pruneEdges(kids[0], depth - 1);
}

function collapseBlankRuns(el: Element, depth = MAX_PRUNE_DEPTH): void {
  let run = 0;
  for (const child of Array.from(el.childNodes)) {
    if (!isBlank(child)) {
      run = 0;
      continue;
    }
    if (child.nodeType === 3) continue;
    run++;
    if (run > 1) child.remove();
  }

  if (depth <= 0) return;
  const kids = Array.from(el.children);
  if (kids.length === 1) collapseBlankRuns(kids[0], depth - 1);
}

function unwrapBlockquote(holder: HTMLElement): void {
  for (let i = 0; i < MAX_UNWRAP_DEPTH; i++) {
    const nodes = Array.from(holder.childNodes).filter((n) => !isBlank(n));
    if (nodes.length !== 1 || nodes[0].nodeType !== 1) return;
    const only = nodes[0] as Element;
    if (only.tagName !== "BLOCKQUOTE") return;
    while (only.firstChild) holder.insertBefore(only.firstChild, only);
    only.remove();
  }
}

function elementLines(el: Element, doc: Document): string[] {
  const tmp = doc.createElement("div");
  tmp.innerHTML = el.innerHTML.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(div|p|tr|li|h[1-6])>/gi, "\n");
  return (tmp.textContent ?? "")
    .split("\n")
    .map((l) => l.replace(/\u00a0/g, " ").trimEnd())
    .filter((l) => l.trim().length > 0);
}

function stripMarkerLine(lines: string[]): string[] {
  if (lines.length && FORWARD_MARKERS.some((re) => re.test(lines[0]))) return lines.slice(1);
  return lines;
}

const HEADER_CONTINUATION = /^[ \t]+\S/;

function parseHeaderLines(lines: string[]): { headers: ForwardHeaders; consumed: number } {
  const headers: ForwardHeaders = { ...EMPTY_HEADERS };
  let consumed = 0;
  let lastKey: keyof ForwardHeaders | null = null;

  for (const line of lines) {
    if (lastKey && HEADER_CONTINUATION.test(line)) {
      const extra = line.trim();
      const prev = headers[lastKey];
      headers[lastKey] = prev ? `${prev} ${extra}` : extra;
      consumed++;
      continue;
    }

    const m = HEADER_LINE.exec(line.trim());
    if (!m) break;
    const key = HEADER_KEYS[m[1].toLowerCase()] ?? null;
    if (key && !headers[key]) headers[key] = m[2].trim() || null;
    lastKey = key;
    consumed++;
  }
  return { headers, consumed };
}

function isHeaderOnly(lines: string[]): boolean {
  if (!lines.length) return false;
  if (!HEADER_LINE.test(lines[0].trim())) return false;
  return lines.every((l) => HEADER_CONTINUATION.test(l) || HEADER_LINE.test(l.trim()));
}

function hasUsableHeaders(h: ForwardHeaders): boolean {
  return Boolean(h.from ?? h.subject);
}

/**
 * Header lines sit either inside the marker element (Gmail) or in sibling
 * elements after it (Outlook, Apple Mail). Collects whichever applies, along
 * with the elements that should be dropped from the rendered body.
 */
function collectForwardHeaders(marker: Element, doc: Document) {
  const markerLines = elementLines(marker, doc);
  let parsed = parseHeaderLines(stripMarkerLine(markerLines));
  const headerEls: Element[] = [marker];

  if (parsed.consumed === 0) {
    const collected: string[] = [];
    let pending: Element[] = [];
    let sib = marker.nextElementSibling;

    for (let i = 0; sib && i < MAX_HEADER_SIBLINGS; i++) {
      const lines = elementLines(sib, doc);
      if (!lines.length) {
        pending.push(sib);
        sib = sib.nextElementSibling;
        continue;
      }
      if (!isHeaderOnly(lines)) break;
      headerEls.push(...pending, sib);
      pending = [];
      collected.push(...lines);
      sib = sib.nextElementSibling;
    }
    if (collected.length) parsed = parseHeaderLines(collected);
  }

  return { headers: parsed.headers, consumed: parsed.consumed, markerLines, headerEls };
}

function stripLeadingLines(el: Element, count: number): void {
  let seen = 0;
  for (const n of Array.from(el.childNodes)) {
    if (seen >= count) break;
    if (n.nodeType === 1) {
      const tag = (n as Element).tagName;
      if (tag === "BR" || BLOCK_TAGS.has(tag)) seen++;
    }
    n.remove();
  }
}

function extractForward(marker: Element, body: HTMLElement, doc: Document): ForwardedBlock | null {
  const { headers, consumed, markerLines, headerEls } = collectForwardHeaders(marker, doc);
  if (!hasUsableHeaders(headers)) return null;

  const holder = extractTail(marker, body, doc);
  const inline = headerEls.length === 1;
  const headerLineCount = 1 + (inline ? consumed : 0);

  if (inline && markerLines.length > headerLineCount) {
    stripLeadingLines(marker, headerLineCount);
  } else {
    headerEls.forEach((el) => el.remove());
  }

  unwrapBlockquote(holder);
  collapseBlankRuns(holder);
  pruneEdges(holder);

  const out = holder.innerHTML.trim();
  return out ? { headers, body: out } : null;
}

function hasVisibleContent(el: HTMLElement): boolean {
  if (dense(el.textContent) > 0) return true;
  return el.querySelector("img, table, video") !== null;
}

export function splitQuotedHtml(html: string): EmailSplit {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const body = doc.body;

  const fwd = findForwardMarker(body);
  const reply = findTextMarker(body);
  const replyLeads = Boolean(fwd && reply && earliest(reply, fwd) === reply);

  if (fwd && !replyLeads) {
    const forwarded = extractForward(fwd, body, doc);
    if (!forwarded) return { main: html, quoted: null, forwarded: null };
    pruneEdges(body);
    return { main: body.innerHTML.trim(), quoted: null, forwarded };
  }

  const marker = findMarker(body, doc);
  if (!marker) return { main: html, quoted: null, forwarded: null };

  const quoted = extractTail(backUpToLead(marker), body, doc).innerHTML;

  if (!hasVisibleContent(body) || dense(quoted.replace(/<[^>]*>/g, "")) === 0) {
    return { main: html, quoted: null, forwarded: null };
  }
  return { main: body.innerHTML.trim(), quoted, forwarded: null };
}

function findTextCut(lines: string[]): number {
  let offset = 0;

  for (let i = 0; i < lines.length; i++) {
    const t = lines[i].trim();
    const isFromHeader =
      FROM_LINE.test(t) && (HEADER_FIELD.test(t) || lines.slice(i + 1, i + 5).some((l) => HEADER_FIELD_LINE.test(l)));

    if (t.startsWith(">") || REPLY_HEADER_MARKERS.some((re) => re.test(t)) || isFromHeader) {
      return offset;
    }
    offset += lines[i].length + 1;
  }
  return -1;
}

function offsetOfLine(lines: string[], index: number): number {
  let offset = 0;
  for (let i = 0; i < index; i++) offset += lines[i].length + 1;
  return offset;
}

export function splitQuotedText(text: string): EmailSplit {
  const lines = text.split("\n");
  const markerIdx = lines.findIndex((l) => FORWARD_MARKERS.some((re) => re.test(l.trim())));
  const cut = findTextCut(lines);

  if (markerIdx !== -1 && (cut === -1 || cut >= offsetOfLine(lines, markerIdx))) {
    return { main: text, quoted: null, forwarded: null };
  }

  if (cut <= 0 || !text.slice(0, cut).trim()) return { main: text, quoted: null, forwarded: null };
  return { main: text.slice(0, cut).trimEnd(), quoted: text.slice(cut), forwarded: null };
}
